import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { QueryError } from 'src/queries/network.utils'
import {
  OrganizationPredictionsSettingsResponse,
  OrganizationSuggestionsSettingsResponse,
  PatchOrganizationPredictionsSettingsRequest,
  PatchOrganizationSuggestionsSettingsRequest,
} from 'src/queries/organizations/automationSettings/automationSettings.models'
import {
  getOrganizationPredictionsSettings,
  getOrganizationSuggestionsSettings,
  patchOrganizationPredictionsSettings,
  patchOrganizationSuggestionsSettings,
} from 'src/queries/organizations/automationSettings/automationSettings.network'
import { useActiveOrgIntegration } from 'src/queries/organizations/integrations.queries'
import { CustomMutationOptions } from 'src/queries/queries.utils'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { isSuggestionsAndPredictionsEnabled } from 'src/utils/featureFlags'

export const automationSettingsQueryKeys = {
  all: () => ['automationSettings'] as const,
  suggestions: (orgId: string | undefined) =>
    [...automationSettingsQueryKeys.all(), 'suggestions', orgId] as const,
  predictions: (orgId: string | undefined) =>
    [...automationSettingsQueryKeys.all(), 'predictions', orgId] as const,
}

export const automationSettingsQueries = {
  suggestions: (orgId: string | undefined) =>
    queryOptions({
      queryKey: automationSettingsQueryKeys.suggestions(orgId),
      queryFn: () => getOrganizationSuggestionsSettings(orgId!),
      enabled: Boolean(orgId),
    }),
  predictions: (orgId: string | undefined) =>
    queryOptions({
      queryKey: automationSettingsQueryKeys.predictions(orgId),
      queryFn: () => getOrganizationPredictionsSettings(orgId!),
      enabled: Boolean(orgId),
    }),
}

export type UpdateSuggestionsParams = {
  orgId: string
  request: PatchOrganizationSuggestionsSettingsRequest
}

export type UpdatePredictionsParams = {
  orgId: string
  request: PatchOrganizationPredictionsSettingsRequest
}

export const automationSettingsMutations = {
  patchSuggestions: () =>
    mutationOptions<
      OrganizationSuggestionsSettingsResponse,
      QueryError,
      UpdateSuggestionsParams
    >({
      mutationFn: (params) =>
        patchOrganizationSuggestionsSettings(params.orgId, params.request),
    }),
  patchPredictions: () =>
    mutationOptions<
      OrganizationPredictionsSettingsResponse,
      QueryError,
      UpdatePredictionsParams
    >({
      mutationFn: (params) =>
        patchOrganizationPredictionsSettings(params.orgId, params.request),
    }),
}

export function usePatchOrganizationSuggestionsSettings(
  options?: CustomMutationOptions<
    OrganizationSuggestionsSettingsResponse,
    QueryError,
    UpdateSuggestionsParams
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...automationSettingsMutations.patchSuggestions(),
    onSuccess: (_data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: automationSettingsQueryKeys.suggestions(variables.orgId),
      })
      onSuccess?.(_data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function usePatchOrganizationPredictionsSettings(
  options?: CustomMutationOptions<
    OrganizationPredictionsSettingsResponse,
    QueryError,
    UpdatePredictionsParams
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...automationSettingsMutations.patchPredictions(),
    onSuccess: (_data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: automationSettingsQueryKeys.predictions(variables.orgId),
      })
      onSuccess?.(_data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useQueryCanUseAIExpenseCategories() {
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: activeErpIntegration, isLoading: isErpIntegrationsLoading } =
    useActiveOrgIntegration({
      orgId: organization?.id,
      enabled: Boolean(organization?.id),
    })
  const isSyncGateOn = isSuggestionsAndPredictionsEnabled(
    remoteConfig,
    organization?.id,
    Boolean(activeErpIntegration),
  )
  const {
    data: aiExpCatsFeatures,
    isLoading: isAIExpCatsLoading,
    error: aiExpCatsError,
  } = useQueryUserFeatures([UserFeature.AI_EXPENSE_CATEGORIES])
  const hasAIExpenseCategoriesFeature = Boolean(
    aiExpCatsFeatures?.AI_EXPENSE_CATEGORIES || false,
  )
  return {
    data: Boolean(isSyncGateOn && hasAIExpenseCategoriesFeature),
    isLoading:
      remoteConfig.show_suggestions_and_predictions &&
      (isErpIntegrationsLoading || isAIExpCatsLoading),
    error: aiExpCatsError,
  }
}
